import React, { useMemo, useRef, useState } from "react";
import {
  Tooltip,
  Typography,
  Button,
  Popover,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Box,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import "../../styles/common/commonFilter.scss";
import TooltipText from "./tooltipText";

const CommonFilter = ({
  sections = [],
  values = {},
  onMultiChange = () => {},
  disabled = false,
  size = "small",
  sx = {},
  style = {},
  className = "",
  showAppliedChips = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const titlesRef = useRef(null);

  const appliedCount = useMemo(() => {
    const vals = Object.values(values || {});
    return vals.filter((v) => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== "" && v != null;
    }).length;
  }, [values]);

  const appliedItems = useMemo(() => {
    return (sections || []).flatMap((section) => {
      const current = (values || {})[section.key];
      const options = Array.isArray(section.options) ? section.options : [];

      if (section.multiple) {
        const selectedValues = Array.isArray(current) ? current : [];
        return selectedValues.map((val) => {
          const option = options.find((item) => item.value === val);
          return {
            key: section.key,
            value: val,
            multiple: true,
            label: `${section.title || section.key}: ${
              option?.label || String(val)
            }`,
          };
        });
      }

      if (current === "" || current == null) return [];
      const option = options.find((item) => item.value === current);
      return [
        {
          key: section.key,
          value: current,
          multiple: false,
          label: `${section.title || section.key}: ${
            option?.label || String(current)
          }`,
        },
      ];
    });
  }, [sections, values]);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelectChange = (key, multiple) => (event) => {
    const val = event.target.value;
    const next = {
      ...(values || {}),
      [key]: multiple ? (Array.isArray(val) ? val : []) : val,
    };
    onMultiChange(next);
    if (!multiple) handleClose();
  };

  const handleReset = () => {
    onMultiChange({});
    handleClose();
  };

  const handleRemoveChip = (key, val, multiple = true) => (e) => {
    e.stopPropagation();
    const current = (values || {})[key];
    const nextVal = multiple
      ? Array.isArray(current)
        ? current.filter((v) => v !== val)
        : []
      : "";
    const next = { ...(values || {}), [key]: nextVal };
    onMultiChange(next);
  };

  return (
    <>
      <Tooltip
        title={appliedCount ? `Filter (${appliedCount})` : "Filter"}
        placement="bottom"
      >
        <span>
          <button
            type="button"
            className={`toolbar-btn common-filter__trigger${
              appliedCount ? " common-filter__trigger--active" : ""
            }${className ? ` ${className}` : ""}`}
            onClick={handleOpen}
            disabled={disabled}
            style={style}
            aria-label="Filter"
          >
            <TuneIcon fontSize="small" className="filter-icon" />
            <span>Filter</span>
            {appliedCount > 0 && (
              <span
                className="common-filter__count"
                aria-label={`${appliedCount} filters applied`}
              >
                {appliedCount}
              </span>
            )}
          </button>
        </span>
      </Tooltip>

      {showAppliedChips && appliedItems.length > 0 && (
        <div className="common-filter__applied-chips" aria-label="Applied filters">
          {appliedItems.map((item) => (
            <button
              key={`${item.key}-${item.value}`}
              type="button"
              className="common-filter__applied-chip"
              onClick={handleRemoveChip(item.key, item.value, item.multiple)}
              title={`Remove ${item.label}`}
            >
              <span>{item.label}</span>
              <span className="common-filter__applied-chip-close">x</span>
            </button>
          ))}
        </div>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "common-filter__titles-paper" }}
      >
        <div ref={titlesRef} className="common-filter__container common-filter">
          <Typography variant="subtitle2" className="common-filter__heading">
            Filter
          </Typography>
          <div className="common-filter__fields">
            {sections.map((section) => (
              <div key={section.key} className="common-filter__field">
                <FormControl
                  fullWidth
                  size="small"
                  className="common-filter__control"
                >
                  {section.title && (
                    <Typography
                      variant="caption"
                      className="common-filter__label"
                      sx={{ mb: 0.5, color: "text.secondary" }}
                    >
                      {section.title}
                    </Typography>
                  )}
                  <Select
                    multiple={!!section.multiple}
                    aria-label={section.title || section.key}
                    value={(() => {
                      const v = (values || {})[section.key];
                      return section.multiple
                        ? Array.isArray(v)
                          ? v
                          : []
                        : v ?? "";
                    })()}
                    onChange={handleSelectChange(
                      section.key,
                      !!section.multiple
                    )}
                    displayEmpty
                    className="common-filter__select"
                    renderValue={(selected) => {
                      const list = Array.isArray(section.options)
                        ? section.options
                        : [];
                      if (section.multiple) {
                        const selArr = Array.isArray(selected) ? selected : [];
                        if (!selArr.length) return section.title;
                        const chips = selArr.map((val) => {
                          const label =
                            list.find((o) => o.value === val)?.label ||
                            String(val);
                          return (
                            <Chip
                              key={String(val)}
                              size="small"
                              label={label}
                              onDelete={handleRemoveChip(
                                section.key,
                                val,
                                true,
                              )}
                              onMouseDown={(e) => e.stopPropagation()}
                              sx={{
                                mr: 0.5,
                                mb: 0.25,
                                bgcolor: "var(--jobs-data-hover-color)",
                                color: "var(--button-text-primary)",
                                borderWidth: 1,
                                borderStyle: "solid",
                                borderColor: "var(--button-text-primary)",
                                "& .MuiChip-label": {
                                  fontSize: "var(--fs-sm)",
                                },
                                "& .MuiChip-deleteIcon": {
                                  color: "var(--button-text-primary)",
                                },
                              }}
                            />
                          );
                        });
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            {chips}
                          </Box>
                        );
                      } else {
                        const found = list.find((o) => o.value === selected);
                        if (!found) return section.title;
                        return (
                          <span className="common-filter__select-value">
                            {found.label}
                            {selected !== "" && (
                              <span className="common-filter__dot" />
                            )}
                          </span>
                        );
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        className: "common-filter__menu-paper",
                        sx: { mt: 0.5 },
                      },
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    }}
                  >
                    {(Array.isArray(section.options)
                      ? section.options
                      : []
                    ).map((opt) => {
                      const v = (values || {})[section.key];
                      const isSelected = section.multiple
                        ? Array.isArray(v) && v.includes(opt.value)
                        : (v ?? "") === opt.value;
                      return (
                        <MenuItem
                          key={String(opt.value)}
                          value={opt.value}
                          className="common-filter__menu-item"
                        >
                          <TooltipText
                            text={String(opt.label || "")}
                            className="common-filter__option-label"
                            maxWidth="180"
                          />
                          {isSelected && (
                            <CheckRoundedIcon className="common-filter__check" />
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
            ))}
          </div>
          <div className="common-filter__reset-wrap">
            <Button
              fullWidth
              variant="text"
              onClick={handleReset}
              className="common-filter__reset-btn"
              sx={{
                textTransform: "none",
                color: "text.secondary",
                fontSize: "12px",
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default CommonFilter;
